import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import type { BannerOf } from "../runtime/bannerMatching";
import {
  type CountdownView,
  useBannerCountdown,
} from "../runtime/useBannerCountdown";
import { useBannerTrigger } from "../runtime/useBannerTrigger";
import { useDashboardBanner } from "../runtime/useDashboardBanner";
import { PopUpBanner, type PopUpVisual } from "../templates/PopUpBanner";
import { SlideInBanner } from "../templates/SlideInBanner";
import { visualOf } from "./bannerIcons";
import {
  BOTTOM_BAR_INSET,
  SLIDE_IN_INSET,
  usePublishedHeight,
} from "./bottomInsets";
import { countdownContent } from "./countdownContent";

type OverlayBanner = BannerOf<{ template: "overlay" }>;

/**
 * The one place over the page a Pop-up or a Slide-in floats in: the runtime
 * picks at most one, and it opens once its trigger fires. A closed banner
 * stays on screen while it animates out, so the next one waits for it.
 */
export const OverlayPlacement = () => {
  const { banner, followCta, close } = useDashboardBanner({
    template: "overlay",
  });
  const fired = useBannerTrigger(banner);
  const [shown, setShown] = useState<OverlayBanner | null>(null);
  // Counts from when the banner opens, its first view.
  const timer = useBannerCountdown(shown);

  useEffect(() => {
    if (banner && fired && !shown) setShown(banner);
  }, [banner, fired, shown]);

  if (!shown) return null;

  const { offerOn, countdown, ...content } = countdownContent(timer, {
    title: shown.title,
    text: shown.text,
    ctaLabel: shown.cta.label,
  });
  const props = {
    ...content,
    // Fired too, or a banner picked again while animating out would come
    // straight back without waiting for its trigger.
    open: fired && banner?.id === shown.id,
    onCtaClick: () => followCta(shown),
    onClose: () => close(shown),
    onExited: () => setShown(null),
  };

  return shown.template === "popUp" ? (
    <PopUpBanner
      {...props}
      visual={popUpVisualOf(shown.visual, timer, offerOn)}
    />
  ) : (
    <SlideInCorner>
      <SlideInBanner
        {...props}
        countdown={countdown}
        variant={shown.style}
        visual={visualOf(shown.visual)}
      />
    </SlideInCorner>
  );
};

/**
 * The bottom corner the Slide-in rises in, measured off the design: 24px from
 * the right and bottom of the screen on desktop, 16px all round on a phone,
 * above the Sticky bottom bar while one shows. The corner takes no clicks of
 * its own, only the card does.
 */
const SlideInCorner = ({ children }: { children: React.ReactNode }) => {
  const corner = useRef<HTMLDivElement>(null);
  usePublishedHeight(corner, SLIDE_IN_INSET);

  return (
    <Box
      ref={corner}
      data-testid="slide-in-placement"
      sx={(theme) => ({
        position: "fixed",
        bottom: `var(${BOTTOM_BAR_INSET}, 0px)`,
        right: 0,
        left: { xs: 0, md: "auto" },
        pl: { xs: 3, md: 0 },
        pr: { xs: 3, md: 4 },
        pb: { xs: 3, md: 4 },
        // Under the app bar and the mobile drawer's backdrop, over the page.
        zIndex: theme.zIndex.appBar - 2,
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      })}
    >
      {children}
    </Box>
  );
};

type PopUp = Extract<OverlayBanner, { template: "popUp" }>;

/**
 * The Pop-up's countdown and offer show while the offer runs; once it has
 * run out, neither: the Pop-up keeps its title and text alone.
 */
const popUpVisualOf = (
  visual: PopUp["visual"],
  { seconds }: CountdownView,
  offerOn: boolean
): PopUpVisual | undefined => {
  if (visual?.type === "countdown") {
    return seconds === null ? undefined : { ...visual, seconds };
  }
  if (visual?.type === "offer") {
    return offerOn ? { ...visual, note: visual.note ?? undefined } : undefined;
  }
  return visualOf(visual);
};
